import { optimizeSvgStrokes } from './svgOptimizer';
import { logger } from '@/lib/logger';

/**
 * Convert SVG to a high-quality PNG thumbnail
 * @param svgUrl - URL of the SVG file
 * @param targetWidth - Target width for the thumbnail
 * @param targetHeight - Target height for the thumbnail
 * @returns Promise that resolves to a data URL of the PNG thumbnail
 */
export async function svgToThumbnail(
  svgUrl: string, 
  targetWidth: number = 400, 
  targetHeight: number = 300
): Promise<string> {
  try {
    // Fetch the SVG content
    const response = await fetch(svgUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch SVG');
    }
    
    const svgText = await response.text();
    
    // Optimize the SVG strokes for better display
    const optimizedSvg = optimizeSvgStrokes(svgText);
    
    // Create a blob URL from the optimized SVG
    const svgBlob = new Blob([optimizedSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    
    try {
      // Load the SVG as an image
      const img = new Image();
      img.src = url;
      
      await new Promise((resolve, reject) => {
        img.onload = () => resolve(null);
        img.onerror = () => reject(new Error('Failed to load SVG'));
      });
      
      // Calculate aspect ratio to maintain proportions
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      let finalWidth = targetWidth;
      let finalHeight = targetHeight;
      
      if (aspectRatio > targetWidth / targetHeight) {
        // Image is wider than target aspect ratio
        finalHeight = targetWidth / aspectRatio;
      } else {
        // Image is taller than target aspect ratio
        finalWidth = targetHeight * aspectRatio;
      }
      
      // Create a high-DPI canvas for better quality
      const scale = 2; // 2x resolution for retina displays
      const canvas = document.createElement('canvas');
      canvas.width = finalWidth * scale;
      canvas.height = finalHeight * scale;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');
      
      // Set high quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw the SVG
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Convert to data URL with high quality
      return canvas.toDataURL('image/png', 0.95);
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    logger.error('Error creating SVG thumbnail:', error);
    throw error;
  }
}